import React, { useState, useEffect, useMemo } from 'react';
import { T } from '../utils/theme';
import { fmtBRL, startOfWeekCuiaba, endOfWeekCuiaba, toCuiabaTimestamp } from '../utils/dateUtils';
import { useData } from '../contexts/DataContext';
import { LargeTitle, SolidCard, SectionHeader, Segmented, Avatar, StatusBadge } from '../components/ui';
import api from '../services/api';
import saqueService from '../services/saqueService';
import { Clock, CheckCircle, XCircle, DollarSign, Calendar, Eye, Settings, ExternalLink } from 'lucide-react';

const TZ_CUIABA = 'America/Cuiaba';

const GestaoSaquesPage = () => {
  const [activeTab, setActiveTab] = useState('pendentes');
  const [saques, setSaques] = useState([]);
  const { barbeiros } = useData();
  const [loading, setLoading] = useState(true);
  const [selectedSaque, setSelectedSaque] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [filtros, setFiltros] = useState({ barbeiroId: '', dataInicio: '', dataFim: '', valorMin: '', valorMax: '' });
  const [formData, setFormData] = useState({ observacao: '', motivo: '', formaPagamento: 'PIX', dadosPagamento: '', barbeiroId: '', valor: '', saldoBarbeiro: null, comprovanteFile: null });
  const [periodoRapido, setPeriodoRapido] = useState('semana');

  const periodoSaques = useMemo(() => {
    const hoje = new Date();
    switch (periodoRapido) {
      case 'semana': return { inicio: startOfWeekCuiaba(hoje), fim: endOfWeekCuiaba(hoje) };
      case 'mes': {
        const f = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_CUIABA, year: 'numeric', month: '2-digit', day: '2-digit' });
        const [y, m] = f.format(hoje).split('-').map(Number);
        return { inicio: new Date(Date.UTC(y, m - 1, 1)), fim: new Date(Date.UTC(y, m, 0, 23, 59, 59, 999)) };
      }
      default: return { inicio: null, fim: null };
    }
  }, [periodoRapido]);

  useEffect(() => { carregarSaques(); }, [activeTab, periodoRapido]);

  const carregarSaques = async () => {
    try {
      setLoading(true);
      let dados = [];
      if (activeTab === 'pendentes') {
        const response = await api.get('/saques/pendentes'); dados = response || [];
      } else {
        const response = await api.get('/saques/historico');
        dados = (response || []).filter(s => {
          if (activeTab === 'pago') return s.status === 'pago';
          if (activeTab === 'rejeitado') return s.status === 'rejeitado';
          return false;
        });
      }
      dados = dados.filter(saque => {
        if (filtros.barbeiroId && String(saque.barbeiroId) !== String(filtros.barbeiroId)) return false;
        if (filtros.valorMin && parseFloat(saque.valor) < parseFloat(filtros.valorMin)) return false;
        if (filtros.valorMax && parseFloat(saque.valor) > parseFloat(filtros.valorMax)) return false;
        if (filtros.dataInicio && new Date(saque.dataSolicitacao) < new Date(filtros.dataInicio)) return false;
        if (filtros.dataFim && new Date(saque.dataSolicitacao) > new Date(filtros.dataFim)) return false;
        return true;
      });
      if (periodoRapido !== 'todos' && periodoSaques.inicio && periodoSaques.fim) {
        const inicioMs = periodoSaques.inicio.getTime(), fimMs = periodoSaques.fim.getTime();
        dados = dados.filter(saque => {
          const ms = toCuiabaTimestamp(saque.dataSolicitacao || saque.updatedAt || saque.createdAt);
          return ms >= inicioMs && ms <= fimMs;
        });
      }
      setSaques(dados);
    } catch (error) { console.error('Erro:', error); setSaques([]); }
    finally { setLoading(false); }
  };

  const abrirModal = (tipo, saque = null) => {
    setModalType(tipo); setSelectedSaque(saque);
    setFormData({ observacao: '', motivo: '', formaPagamento: saque?.formaPagamento || 'PIX', dadosPagamento: '', barbeiroId: '', valor: '', saldoBarbeiro: null, comprovanteFile: null });
    setShowModal(true);
  };

  const handleAprovar = async () => {
    if (!formData.formaPagamento || !formData.comprovanteFile) { alert('Preencha forma de pagamento e comprovante!'); return; }
    try {
      const fd = new FormData();
      fd.append('formaPagamento', formData.formaPagamento);
      fd.append('observacao', formData.observacao || '');
      fd.append('comprovante', formData.comprovanteFile);
      await saqueService.aprovarSaque(selectedSaque.id, fd);
      alert('Saque aprovado!'); setShowModal(false); setPeriodoRapido('todos'); setActiveTab('pago');
    } catch (error) { alert(error.response?.data?.error || 'Erro ao aprovar'); }
  };

  const handleRejeitar = async () => {
    if (!formData.motivo || formData.motivo.length < 10) { alert('Motivo obrigatorio (10+ caracteres)!'); return; }
    try {
      await api.post(`/saques/rejeitar/${selectedSaque.id}`, { observacao: formData.motivo });
      alert('Rejeitado'); setShowModal(false); carregarSaques();
    } catch (error) { alert(error.response?.data?.error || 'Erro'); }
  };

  const handleEnviarComissao = async () => {
    if (!formData.barbeiroId || !formData.valor || formData.valor < 1) { alert('Preencha barbeiro e valor!'); return; }
    if (!formData.formaPagamento || !formData.comprovanteFile) { alert('Preencha forma de pagamento e comprovante!'); return; }
    try {
      const fd = new FormData();
      fd.append('barbeiroId', formData.barbeiroId);
      fd.append('valor', parseFloat(formData.valor));
      fd.append('formaPagamento', formData.formaPagamento);
      fd.append('observacao', formData.observacao || '');
      fd.append('comprovante', formData.comprovanteFile);
      await saqueService.enviarComissao(fd);
      alert('Comissao enviada!'); setShowModal(false); setPeriodoRapido('todos'); setActiveTab('pago');
    } catch (error) { alert(error.response?.data?.error || 'Erro'); }
  };

  const formatarData = (data) => new Date(data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const getStatusColor = (status) => {
    const map = { pendente: { bg: 'rgba(255,159,10,0.14)', color: T.warning }, pago: { bg: 'rgba(48,209,88,0.14)', color: T.success }, rejeitado: { bg: 'rgba(255,59,48,0.14)', color: T.danger } };
    return map[status] || map.pendente;
  };

  const inputStyle = { width: '100%', padding: '14px 16px', borderRadius: 14, background: T.fill, border: `0.5px solid ${T.separator}`, color: T.label, fontSize: 16, fontFamily: 'inherit', outline: 'none' };

  return (
    <div style={{ padding: '0 0 40px' }}>
      <LargeTitle title="Saques" subtitle="Gerencie solicitacoes"
        trailing={
          <button onClick={() => abrirModal('enviar')} style={{
            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            padding: '10px 18px', borderRadius: 14, background: T.purple, color: '#fff',
            fontFamily: 'inherit', fontSize: 14, fontWeight: 600,
            boxShadow: '0 4px 12px rgba(191,90,242,0.4)',
          }}><DollarSign size={16} /> Enviar</button>
        }
      />

      {/* Period + Tabs */}
      <div style={{ padding: '0 20px 8px' }}>
        <Segmented
          items={[{ value: 'semana', label: 'Semana' }, { value: 'mes', label: 'Mes' }, { value: 'todos', label: 'Todos' }]}
          value={periodoRapido} onChange={setPeriodoRapido} size="sm"
        />
      </div>
      <div style={{ padding: '0 20px 0' }}>
        <Segmented
          items={[{ value: 'pendentes', label: 'Pendentes' }, { value: 'pago', label: 'Finalizados' }, { value: 'rejeitado', label: 'Rejeitados' }]}
          value={activeTab} onChange={setActiveTab}
        />
      </div>

      <div style={{ padding: '12px 20px 0' }}>
        {loading ? (
          <SolidCard padding={48} radius={20}><div style={{ textAlign: 'center', color: T.label3 }}>Carregando...</div></SolidCard>
        ) : saques.length === 0 ? (
          <SolidCard padding={48} radius={20}><div style={{ textAlign: 'center', color: T.label3, fontSize: 15 }}>Nenhum saque encontrado</div></SolidCard>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {saques.map((saque) => {
              const sc = getStatusColor(saque.status);
              return (
                <SolidCard key={saque.id} padding={16} radius={18}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: T.label }}>{saque.barbeiro?.nome || 'Barbeiro'}</div>
                      <div style={{ fontSize: 11, color: T.label3, marginTop: 2 }}>ID: #{saque.id}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: sc.bg, color: sc.color }}>
                      {saque.status === 'pendente' ? 'PENDENTE' : saque.status === 'pago' ? 'PAGO' : 'REJEITADO'}
                    </span>
                  </div>
                  <div style={{ background: 'rgba(191,90,242,0.08)', borderRadius: 14, padding: '10px 14px', marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: T.purple, fontWeight: 600 }}>Valor</div>
                    <div className="tnum" style={{ fontSize: 22, fontWeight: 700, color: T.purple }}>{fmtBRL(parseFloat(saque.valor))}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.label2 }}>
                    <Calendar size={13} /> {formatarData(saque.dataSolicitacao)}
                  </div>
                  {saque.observacao && <div style={{ fontSize: 11, color: T.label3, marginTop: 4, padding: '4px 8px', background: T.fill, borderRadius: 8 }}>Obs: {saque.observacao}</div>}
                  {saque.selfieUrl && activeTab === 'pendentes' && (
                    <button onClick={() => window.open(saque.selfieUrl, '_blank')} style={{
                      width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 10,
                      border: 'none', cursor: 'pointer', background: T.accentSoft, color: T.accent,
                      fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                    }}>Ver Selfie</button>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {activeTab === 'pendentes' && (
                      <>
                        <button onClick={() => abrirModal('aprovar', saque)} style={{
                          flex: 1, padding: '10px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                          background: T.success, color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                        }}>Aprovar</button>
                        <button onClick={() => abrirModal('rejeitar', saque)} style={{
                          flex: 1, padding: '10px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
                          background: T.danger, color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                        }}>Rejeitar</button>
                      </>
                    )}
                    <button onClick={() => abrirModal('detalhes', saque)} style={{
                      width: 40, height: 40, borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: T.fill, color: T.label, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}><Eye size={16} /></button>
                  </div>
                </SolidCard>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="bx-sheet" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={(e) => e.stopPropagation()} className="bx-sheet-panel" style={{ background: T.surface, borderRadius: '24px 24px 0 0', width: '100%', maxWidth: 500, maxHeight: '85vh', overflow: 'auto', boxShadow: T.shadowStrong }}>
            <div style={{ padding: 20, borderBottom: `0.5px solid ${T.separator}` }}>
              <div className="bx-grabber" style={{ width: 36, height: 4, borderRadius: 2, background: T.fill2, margin: '0 auto 16px' }} />
              <div style={{ fontSize: 22, fontWeight: 700, color: T.label }}>
                {modalType === 'aprovar' && 'Aprovar Saque'}
                {modalType === 'rejeitar' && 'Rejeitar Saque'}
                {modalType === 'enviar' && 'Enviar Comissao'}
                {modalType === 'detalhes' && 'Detalhes'}
              </div>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {modalType === 'aprovar' && (
                <>
                  <div style={{ fontSize: 14, color: T.label }}>Aprovar saque de <strong>{fmtBRL(parseFloat(selectedSaque?.valor || 0))}</strong>?</div>
                  <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 6 }}>Forma de Pagamento *</label>
                    <select value={formData.formaPagamento} onChange={(e) => setFormData({...formData, formaPagamento: e.target.value})} style={inputStyle}>
                      <option value="">Selecione</option><option value="PIX">PIX</option><option value="Transferencia">Transferencia</option><option value="Dinheiro">Dinheiro</option><option value="Cartao">Cartao</option>
                    </select></div>
                  <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 6 }}>Comprovante *</label>
                    <input type="file" accept=".jpeg,.jpg,.png,.gif,.pdf" onChange={(e) => e.target.files[0] && setFormData({...formData, comprovanteFile: e.target.files[0]})} style={inputStyle} />
                    {formData.comprovanteFile && <div style={{ fontSize: 11, color: T.success, marginTop: 4 }}>Arquivo: {formData.comprovanteFile.name}</div>}</div>
                  <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 6 }}>Observacao</label>
                    <textarea value={formData.observacao} onChange={(e) => setFormData({...formData, observacao: e.target.value})} placeholder="Opcional" style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} /></div>
                </>
              )}
              {modalType === 'rejeitar' && (
                <>
                  <textarea value={formData.motivo} onChange={(e) => setFormData({...formData, motivo: e.target.value})} placeholder="Motivo * (10+ caracteres)" style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }} />
                  <div style={{ fontSize: 11, color: T.label3 }}>{formData.motivo.length}/10</div>
                </>
              )}
              {modalType === 'enviar' && (
                <>
                  <select value={formData.barbeiroId} onChange={async (e) => {
                    const id = e.target.value; setFormData({...formData, barbeiroId: id});
                    if (id) { try { const b = await api.barbeiros.getById(id); setFormData(prev => ({...prev, saldoBarbeiro: b.saldoDisponivel || 0})); } catch { setFormData(prev => ({...prev, saldoBarbeiro: 0})); } }
                    else setFormData(prev => ({...prev, saldoBarbeiro: null}));
                  }} style={inputStyle}>
                    <option value="">Escolha barbeiro</option>
                    {barbeiros.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}
                  </select>
                  {formData.saldoBarbeiro !== null && (
                    <div style={{ background: 'rgba(191,90,242,0.08)', borderRadius: 14, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: T.purple }}>Saldo disponivel</div>
                      <div className="tnum" style={{ fontSize: 18, fontWeight: 700, color: T.purple }}>{fmtBRL(formData.saldoBarbeiro)}</div>
                    </div>
                  )}
                  <input type="number" step="0.01" value={formData.valor} onChange={(e) => setFormData({...formData, valor: e.target.value})} placeholder="Valor *" style={inputStyle} />
                  <select value={formData.formaPagamento} onChange={(e) => setFormData({...formData, formaPagamento: e.target.value})} style={inputStyle}>
                    <option value="">Forma Pagamento</option><option value="PIX">PIX</option><option value="Transferencia">Transferencia</option><option value="Dinheiro">Dinheiro</option><option value="Cartao">Cartao</option>
                  </select>
                  <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: T.label2, marginBottom: 6 }}>Comprovante *</label>
                    <input type="file" accept=".jpeg,.jpg,.png,.gif,.pdf" onChange={(e) => e.target.files[0] && setFormData({...formData, comprovanteFile: e.target.files[0]})} style={inputStyle} />
                    {formData.comprovanteFile && <div style={{ fontSize: 11, color: T.success, marginTop: 4 }}>Arquivo: {formData.comprovanteFile.name}</div>}</div>
                  <textarea value={formData.observacao} onChange={(e) => setFormData({...formData, observacao: e.target.value})} placeholder="Obs (opcional)" style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
                </>
              )}
              {modalType === 'detalhes' && selectedSaque && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[['ID', `#${selectedSaque.id}`], ['Barbeiro', selectedSaque.barbeiro?.nome], ['Tipo', selectedSaque.tipo === 'solicitacao' ? 'Solicitado' : 'Gestor'], ['Valor', fmtBRL(parseFloat(selectedSaque.valor))], ['Data', formatarData(selectedSaque.dataSolicitacao)]].map(([label, val]) => (
                    <div key={label} style={{ paddingBottom: 8, borderBottom: `0.5px solid ${T.separator}` }}>
                      <div style={{ fontSize: 11, color: T.label3 }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.label }}>{val}</div>
                    </div>
                  ))}
                  {selectedSaque.formaPagamento && <div style={{ paddingBottom: 8, borderBottom: `0.5px solid ${T.separator}` }}><div style={{ fontSize: 11, color: T.label3 }}>Forma</div><div style={{ fontSize: 14, fontWeight: 600, color: T.label }}>{selectedSaque.formaPagamento}</div></div>}
                  {selectedSaque.selfieUrl && <a href={selectedSaque.selfieUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: T.accent, display: 'flex', alignItems: 'center', gap: 4 }}><ExternalLink size={14} /> Ver selfie</a>}
                  {selectedSaque.comprovanteUrl && <a href={selectedSaque.comprovanteUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: T.accent, display: 'flex', alignItems: 'center', gap: 4 }}><ExternalLink size={14} /> Ver comprovante</a>}
                  {selectedSaque.observacao && <div><div style={{ fontSize: 11, color: T.label3 }}>Obs</div><div style={{ fontSize: 13, color: T.label }}>{selectedSaque.observacao}</div></div>}
                </div>
              )}
            </div>
            <div style={{ padding: 20, display: 'flex', gap: 10, borderTop: `0.5px solid ${T.separator}` }}>
              {modalType === 'aprovar' && <><button onClick={handleAprovar} style={{ flex: 1, padding: 15, borderRadius: 14, border: 'none', background: T.success, color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Aprovar</button><button onClick={() => setShowModal(false)} style={{ padding: '15px 20px', borderRadius: 14, border: `1px solid ${T.separator}`, background: 'transparent', color: T.label, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button></>}
              {modalType === 'rejeitar' && <><button onClick={handleRejeitar} style={{ flex: 1, padding: 15, borderRadius: 14, border: 'none', background: T.danger, color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Rejeitar</button><button onClick={() => setShowModal(false)} style={{ padding: '15px 20px', borderRadius: 14, border: `1px solid ${T.separator}`, background: 'transparent', color: T.label, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button></>}
              {modalType === 'enviar' && <><button onClick={handleEnviarComissao} style={{ flex: 1, padding: 15, borderRadius: 14, border: 'none', background: T.purple, color: '#fff', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(191,90,242,0.4)' }}>Enviar</button><button onClick={() => setShowModal(false)} style={{ padding: '15px 20px', borderRadius: 14, border: `1px solid ${T.separator}`, background: 'transparent', color: T.label, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancelar</button></>}
              {modalType === 'detalhes' && <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: 15, borderRadius: 14, border: 'none', background: T.fill, color: T.label, fontFamily: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Fechar</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestaoSaquesPage;
